import { ContentLink, EpiBlock, EpiContent, EpiLink } from '@api/types';
import { ContentArea } from '@components/cms';
import { SmartLink } from '@components/common';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Button,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
} from '@mui/material';
import { colors } from '@themes/colors';
import { DropdownMenuProvider, useDropdownMenuContext } from './DropdownMenuContext';

// TYPES
//-------
export type DropdownMenuBlockProps = {
  buttonLabel: string;
  content?: EpiContent;
  contentLink?: ContentLink;
  primaryLinks?: EpiLink[];
};

// COMPONENTS
//------------
export const DropdownMenuBlock = ({
  content = [],
  buttonLabel,
  contentLink,
  primaryLinks,
}: DropdownMenuBlockProps) => {
  return (
    <DropdownMenuProvider
      content={content}
      buttonLabel={buttonLabel}
      contentLink={contentLink}
      primaryLinks={primaryLinks}
    >
      <DropdownMenu />
    </DropdownMenuProvider>
  );
};

const DropdownMenu = () => {
  const { handleClick, contentLink, buttonLabel } = useDropdownMenuContext();

  const id = contentLink?.guidValue ?? '';

  return (
    <>
      <Button
        variant='text'
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleClick}
        sx={{
          color: colors.text.primary,
          fontWeight: '500 !important',
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
          padding: '0 !important',
          minWidth: 'fit-content !important',
        }}
        disableRipple
        disableFocusRipple
      >
        {buttonLabel}
      </Button>
      <MenuList id={id} />
    </>
  );
};
const MenuList = ({ id }: { id: string }) => {
  const { anchorEl, isOpen, handleOpenToggle, content, primaryLinks } = useDropdownMenuContext();

  return (
    <Popper id={id} open={isOpen} anchorEl={anchorEl} placement='bottom-end' sx={{ zIndex: 1199 }}>
      <ClickAwayListener onClickAway={handleOpenToggle}>
        <Paper
          sx={{
            width: 'max-content',
            overflow: 'auto',
          }}
        >
          <List sx={{ maxHeight: 400 }}>
            <ContentArea
              content={content}
              wrapperComponent={(props) => <ContentItem {...props} />}
            />
            {primaryLinks?.map((link, index) => (
              <ListItem key={index} dense onClick={handleOpenToggle}>
                <SmartLink
                  href={link.href}
                  sx={{
                    width: '100%',
                    '& .link-text': {
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      color: colors.text.primary,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <ListItemText primary={link.text} sx={{ cursor: 'pointer' }} />
                </SmartLink>
              </ListItem>
            ))}
          </List>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

const ContentItem = ({ block }: { block: EpiBlock }) => {
  const { handleOpenToggle } = useDropdownMenuContext();
  return block?.content?.map(({ contentLink }: { contentLink: ContentLink }) => {
    const code = contentLink?.expanded?.code;
    const displayName = contentLink?.expanded?.displayName;
    const url = contentLink?.expanded?.url || '';

    return (
      <ListItem key={code} dense onClick={handleOpenToggle}>
        <SmartLink
          href={url}
          sx={{
            width: '100%',
            '& .link-text': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: colors.text.primary,
              backgroundColor: 'transparent',
            },
          }}
        >
          <ListItemText primary={displayName} sx={{ cursor: 'pointer' }} />
        </SmartLink>
      </ListItem>
    );
  });
};
